import { __rest, __assign } from 'tslib';
import React, { useRef, useState, useEffect } from 'react';
import mergeRefs from 'react-merge-refs';
import cn from 'classnames';
import { getDataTestId } from '@alfalab/core-components-shared/esm';
import { Spinner } from '@alfalab/core-components-spinner/esm';
import { useFocus } from '@alfalab/hooks';

var defaultColors = {"accent":"button__accent_mf3bx","loader":"button__loader_mf3bx","primary":"button__primary_mf3bx","secondary":"button__secondary_mf3bx","outlined":"button__outlined_mf3bx","tertiary":"button__tertiary_mf3bx","filled":"button__filled_mf3bx","transparent":"button__transparent_mf3bx","link":"button__link_mf3bx","text":"button__text_mf3bx","ghost":"button__ghost_mf3bx","component":"button__component_mf3bx","loading":"button__loading_mf3bx"};
require('./default.css')

var commonStyles = {"component":"button__component_1yzjy","hug":"button__hug_1yzjy","fill":"button__fill_1yzjy","hint":"button__hint_1yzjy","allowBackdropBlur":"button__allowBackdropBlur_1yzjy","secondary":"button__secondary_1yzjy","accent":"button__accent_1yzjy","primary":"button__primary_1yzjy","focused":"button__focused_1yzjy","loading":"button__loading_1yzjy","label":"button__label_1yzjy","addons":"button__addons_1yzjy","stretchText":"button__stretchText_1yzjy","loader":"button__loader_1yzjy","size-32":"button__size-32_1yzjy","iconOnly":"button__iconOnly_1yzjy","size-40":"button__size-40_1yzjy","size-48":"button__size-48_1yzjy","size-56":"button__size-56_1yzjy","size-64":"button__size-64_1yzjy","size-72":"button__size-72_1yzjy","withRightAddons":"button__withRightAddons_1yzjy","ghost":"button__ghost_1yzjy","text":"button__text_1yzjy","withLeftAddons":"button__withLeftAddons_1yzjy","link":"button__link_1yzjy","transparent":"button__transparent_1yzjy","block":"button__block_1yzjy","nowrap":"button__nowrap_1yzjy","rounded":"button__rounded_1yzjy"};
require('./index.css')

var invertedColors = {"accent":"button__accent_uyphh","loader":"button__loader_uyphh","primary":"button__primary_uyphh","secondary":"button__secondary_uyphh","outlined":"button__outlined_uyphh","tertiary":"button__tertiary_uyphh","filled":"button__filled_uyphh","transparent":"button__transparent_uyphh","link":"button__link_uyphh","text":"button__text_uyphh","ghost":"button__ghost_uyphh","component":"button__component_uyphh","loading":"button__loading_uyphh"};
require('./inverted.css')

var colorStyles = {
    default: defaultColors,
    inverted: invertedColors,
};
/**
 * Минимальное время отображения лоадера - 500мс,
 * чтобы при быстрых ответах от сервера кнопка не «моргала».
 */
var LOADER_MIN_DISPLAY_INTERVAL = 500;
var logWarning = function (view) {
    if (process.env.NODE_ENV !== 'development') {
        return;
    }
    var viewsMap = {
        link: 'transparent',
        ghost: 'text',
    };
    // eslint-disable-next-line no-console
    console.warn(
    // eslint-disable-next-line prefer-template
    "@alfalab/core-components/button: view='".concat(view, "' \u0431\u0443\u0434\u0435\u0442 \u0443\u0434\u0430\u043B\u0435\u043D \u0432 \u0441\u043B\u0435\u0434\u0443\u044E\u0449\u0438\u0445 \u043C\u0430\u0436\u043E\u0440\u043D\u044B\u0445 \u0432\u0435\u0440\u0441\u0438\u044F\u0445. ") +
        "\u0418\u0441\u043F\u043E\u043B\u044C\u0437\u0443\u0439\u0442\u0435 view='".concat(viewsMap[view], "'. \u0427\u0442\u043E\u0431\u044B \u043F\u043E\u043C\u0435\u043D\u044F\u0442\u044C \u0432\u0441\u0435 \u043A\u043D\u043E\u043F\u043A\u0438 \u043D\u0430 \u043F\u0440\u043E\u0435\u043A\u0442\u0435 \u0440\u0430\u0437\u043E\u043C, \u043C\u043E\u0436\u043D\u043E \u0432\u043E\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u044C\u0441\u044F codemod: ") +
        'npx @alfalab/core-components-codemod --transformers=button-views-45 src/**/*.tsx');
};
var SIZE_TO_CLASSNAME_MAP = {
    xxs: 'size-32',
    xs: 'size-40',
    s: 'size-48',
    m: 'size-56',
    l: 'size-64',
    xl: 'size-72',
    32: 'size-32',
    40: 'size-40',
    48: 'size-48',
    56: 'size-56',
    64: 'size-64',
    72: 'size-72',
};
var BaseButton = React.forwardRef(function (_a, ref) {
    var _b, _c, _d;
    var allowBackdropBlur = _a.allowBackdropBlur, children = _a.children, _e = _a.view, view = _e === void 0 ? 'secondary' : _e, _f = _a.shape, shape = _f === void 0 ? 'rectangular' : _f, _g = _a.textResizing, textResizing = _g === void 0 ? 'hug' : _g, hint = _a.hint, leftAddons = _a.leftAddons, rightAddons = _a.rightAddons, _h = _a.size, size = _h === void 0 ? 56 : _h, _j = _a.block, block = _j === void 0 ? false : _j, className = _a.className, spinnerClassName = _a.spinnerClassName, dataTestId = _a.dataTestId, href = _a.href, _k = _a.loading, loading = _k === void 0 ? false : _k, _l = _a.nowrap, nowrap = _l === void 0 ? false : _l, _m = _a.colors, colors = _m === void 0 ? 'default' : _m, _o = _a.Component, Component = _o === void 0 ? href ? 'a' : 'button' : _o, onClick = _a.onClick, _p = _a.styles, styles = _p === void 0 ? {} : _p, _q = _a.colorStylesMap, colorStylesMap = _q === void 0 ? { default: {}, inverted: {} } : _q, restProps = __rest(_a, ["allowBackdropBlur", "children", "view", "shape", "textResizing", "hint", "leftAddons", "rightAddons", "size", "block", "className", "spinnerClassName", "dataTestId", "href", "loading", "nowrap", "colors", "Component", "onClick", "styles", "colorStylesMap"]);
    if (['link', 'ghost'].includes(view)) {
        logWarning(view);
    }
    var buttonRef = useRef(null);
    var focused = useFocus(buttonRef, 'keyboard')[0];
    var _r = useState(true), loaderTimePassed = _r[0], setLoaderTimePassed = _r[1];
    var timerId = useRef(0);
    var showLoader = loading || !loaderTimePassed;
    var showHint = hint && ['size-56', 'size-64', 'size-72'].includes(SIZE_TO_CLASSNAME_MAP[size]);
    var iconOnly = !children;
    var componentProps = {
        className: cn(commonStyles.component, commonStyles[view], commonStyles[SIZE_TO_CLASSNAME_MAP[size]], commonStyles[textResizing], shape === 'rectangular' && styles[SIZE_TO_CLASSNAME_MAP[size]], shape === 'rounded' && commonStyles[shape], colorStyles[colors].component, colorStyles[colors][view], colorStylesMap[colors].component, (_b = {},
            _b[commonStyles.allowBackdropBlur] = allowBackdropBlur,
            _b[colorStylesMap[colors][view]] = Boolean(colorStylesMap[colors][view]),
            _b[commonStyles.focused] = focused,
            _b[commonStyles.block] = block,
            _b[commonStyles.iconOnly] = iconOnly,
            _b[commonStyles.loading] = showLoader,
            _b[commonStyles.withRightAddons] = Boolean(rightAddons) && !iconOnly,
            _b[commonStyles.withLeftAddons] = Boolean(leftAddons) && !iconOnly,
            _b[colorStyles[colors].loading] = showLoader,
            _b[colorStylesMap[colors].loading] = showLoader,
            _b), className),
        'data-test-id': dataTestId || null,
    };
    var _s = restProps, disabled = _s.disabled, _t = _s.type, type = _t === void 0 ? 'button' : _t, restButtonProps = __rest(_s, ["disabled", "type"]);
    var buttonChildren = (React.createElement(React.Fragment, null,
        leftAddons && React.createElement("span", { className: commonStyles.addons }, leftAddons),
        children && (React.createElement("span", { className: cn(commonStyles.label, (_c = {},
                _c[commonStyles.nowrap] = nowrap,
                _c[commonStyles.stretchText] = !(leftAddons || rightAddons) || textResizing === 'fill',
                _c)) },
            children,
            showHint && React.createElement("span", { className: commonStyles.hint }, hint))),
        showLoader && (React.createElement(Spinner, { dataTestId: getDataTestId(dataTestId, 'loader'), visible: true, className: cn(commonStyles.loader, colorStyles[colors].loader, colorStylesMap[colors].loader, spinnerClassName) })),
        rightAddons && React.createElement("span", { className: commonStyles.addons }, rightAddons)));
    useEffect(function () {
        if (loading) {
            setLoaderTimePassed(false);
            timerId.current = window.setTimeout(function () {
                setLoaderTimePassed(true);
            }, LOADER_MIN_DISPLAY_INTERVAL);
        }
    }, [loading]);
    useEffect(function () { return function () {
        window.clearTimeout(timerId.current);
    }; }, []);
    var handleClick = function (e) {
        if (disabled || showLoader) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    if (href) {
        var target = restProps.target;
        // Для совместимости с react-router-dom, меняем href на to
        var hrefProps = (_d = {}, _d[typeof Component === 'string' ? 'href' : 'to'] = href, _d);
        return (React.createElement(Component, __assign({ rel: target === '_blank' ? 'noreferrer noopener' : undefined }, componentProps, restProps, hrefProps, { onClick: handleClick, disabled: disabled || showLoader, ref: mergeRefs([buttonRef, ref]) }), buttonChildren));
    }
    return (React.createElement(Component, __assign({}, componentProps, restButtonProps, { onClick: handleClick, type: type, disabled: disabled || showLoader, ref: mergeRefs([buttonRef, ref]) }), buttonChildren));
});

export { BaseButton, LOADER_MIN_DISPLAY_INTERVAL };
